import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTop — restores window scroll position to (0, 0) on every pathname change.
 *
 * Behaviour:
 *   - Triggers on pathname change only (not query/hash).
 *   - Skips when navigating to a same-page hash anchor (preserves anchor jumps).
 *   - Uses `instant` behaviour to avoid janky animation on every navigation.
 *   - Disables the browser's automatic scroll restoration so back/forward
 *     also start at the top on a fresh visit (browser still restores on
 *     pop-state when the user explicitly hits back — that's intentional).
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    if (hash) return; // let the browser anchor-jump handle hash links
    // Use instant to avoid jitter on cross-page navigation
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}
